import Head from 'next/head';
import { useRouter } from 'next/router';
import fetchPageMetaInfo from '@api/fetchers/pageMetaInfo';
import SEO from '@seo/index';
import TestSeriesListing from '@cont/SeeAll/TestSeriesListing/TestSeriesListing';
import subGoalSchema from '@seo/goal/subGoal';
import { useSetSelectedNav } from '@stores/sidenav';
import plusNavBarItems from '@layout/NavBar/PlusNavBar/plusNavBarItems';
import { getValidTestType } from '@cont/SeeAll/utils';
import { ALL_TEST_SERIES } from '@constants/segment';
import { emptyObject } from '@constants/empty';
import Layout from '@layout/index';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import { usePageSeoContent } from '@stores/AppCommonData';
import resolvePromiseToObject from '@utils/resolvePromiseToObj';
import { SWRConfig } from 'swr';
import {
  fetchTestSeriesData,
  fetchContestOnboardingPreference,
  fetchCombatsData
} from '@api/fetchers/goal/test-series';
import { fetchGoalInfo } from '@api/fetchers/goal';
import useFullTestSeriesInfo from '@api/hooks/goal/test-series/useFullTestSeriesInfo';
import { COMBAT_TYPES } from '@cont/Combat/constants';

const TestSeries = ({ testType, fallbackData = {}, combatsPreference }) => {
  const router = useRouter();
  const {
    query: { goalUID }
  } = router;

  const { title, description } = usePageSeoContent();

  const { keys, goalInfo } = useFullTestSeriesInfo(
    goalUID,
    testType,
    fallbackData,
    'Tests',
    combatsPreference,
    COMBAT_TYPES.UPCOMING
  );

  const { slug: goalSlug, uid, thumbnail, ogImage } = goalInfo || emptyObject;

  useSetSelectedNav(plusNavBarItems.TEST_SERIES);

  const metaTitle = title || `Test Series | ${goalInfo?.title} | Unacademy`;
  const canonicalURL = `https://unacademy.com/goal/${goalSlug}/${uid}/test-series`;

  return (
    <>
      <SWRConfig
        value={{
          revalidateOnMount: false,
          dedupingInterval: 60 * 1000,
          fallback: {
            [keys.testSeriesSWRKey]: [fallbackData?.testSeriesData],
            [keys.combatsSWRKey]: [fallbackData?.combatsData],
            [keys.goalInfoSWRKey]: fallbackData?.goalData,
            [keys.contestOnboardingPreferenceSWRKey]:
              fallbackData?.contestOnboardingPreferenceData
          }
        }}
      >
        <SEO
          title={metaTitle}
          description={description}
          url={canonicalURL}
          openGraph={{
            title,
            description,
            images: [
              {
                url: ogImage || thumbnail
              }
            ],
            url: canonicalURL
          }}
          schema={subGoalSchema({
            goal: goalInfo,
            description,
            subGoal: { name: 'Test series', link: 'test-series' }
          })}
        />
        <Head>
          <title>{metaTitle}</title>
          <link rel="canonical" href={canonicalURL} />
        </Head>
        <TestSeriesListing goalName={goalInfo?.title} />
      </SWRConfig>
    </>
  );
};

TestSeries.pageInfo = {
  isMWebSupported: true,
  name: ALL_TEST_SERIES,
  layoutOptions: {
    hideFooter: true,
    showPlusNav: true
  },
  isCMSMeta: true
};

export const getStaticProps = async ({ params }) => {
  try {
    const { goalUID, goalSlug } = params || {};
    // Handle invalid sitemap requests
    if (!goalUID || !goalSlug) return { notFound: true };
    const goalInfo = await fetchGoalInfo(goalUID);
    if (!goalInfo || !!goalInfo.errorCode) return { notFound: true };
    const pageMetaInfo = await fetchPageMetaInfo(ALL_TEST_SERIES, goalUID);
    const contestOnboardingPreferenceData =
      await fetchContestOnboardingPreference(goalUID);
    // Redirect if platform goal
    if (goalInfo.isPlatformGoal)
      return {
        redirect: {
          destination: `/goal/${goalInfo.slug || goalSlug}/${goalUID}`,
          permanent: true
        }
      };

    // Considering default testType during static build time
    const initialType = getValidTestType();

    const promises = [
      resolvePromiseToObject(
        fetchTestSeriesData(initialType, goalUID),
        'testSeriesData'
      )
    ];

    // We are considering the preference as the the first option in the preferences received, and
    // UPCOMING as the default selector
    const defaultPreference =
      contestOnboardingPreferenceData?.[0]?.options?.[0]?.uid;
    if (contestOnboardingPreferenceData?.length) {
      promises.push(
        resolvePromiseToObject(
          fetchCombatsData(goalUID, defaultPreference, COMBAT_TYPES.UPCOMING),
          'combatsData'
        )
      );
    }

    const fallbackData = await Promise.all(promises).then((resolvedValues) =>
      resolvedValues.reduce(
        (acc, next) => ({ ...acc, [next.type]: next.result }),
        {}
      )
    );

    return {
      props: {
        fallbackData: {
          ...fallbackData,
          goalData: goalInfo,
          contestOnboardingPreferenceData
        },
        combatsPreference: defaultPreference || null,
        testType: initialType,
        pageMetaInfo
      }
    };
  } catch (error) {
    return { notFound: true, revalidate: 60 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

export default TestSeries;
TestSeries.Layout = Layout;
TestSeries.Wrapper = PageReduxProvider;
